import Alpine from "alpinejs";
import panzoom, { type PanZoom, type Transform } from "panzoom";
import { attachImageUpdater, type PanzoomModule } from "./images";
import type { ViewportStore } from "./viewport";

const modules: PanzoomModule[] = [attachImageUpdater];

const FIT_PADDING = 0.9;
/** amator default duration used by panzoom smooth pan/zoom. */
const SMOOTH_MS = 420;

type SceneBounds = {
    left: number;
    top: number;
    right: number;
    bottom: number;
};

function getSceneBounds(scene: SVGGElement, target: SVGGraphicsElement): SceneBounds | null {
    const bbox = target.getBBox();
    if (bbox.width === 0 || bbox.height === 0) {
        return null;
    }

    const sceneCtm = scene.getCTM();
    const targetCtm = target.getCTM();
    if (!sceneCtm || !targetCtm) {
        return null;
    }

    const toScene = sceneCtm.inverse().multiply(targetCtm);
    const svg = scene.ownerSVGElement;
    if (!svg) {
        return null;
    }

    const corners = [
        { x: bbox.x, y: bbox.y },
        { x: bbox.x + bbox.width, y: bbox.y },
        { x: bbox.x, y: bbox.y + bbox.height },
        { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
    ].map((c) => {
        const pt = svg.createSVGPoint();
        pt.x = c.x;
        pt.y = c.y;
        return pt.matrixTransform(toScene);
    });

    const xs = corners.map((p) => p.x);
    const ys = corners.map((p) => p.y);
    return {
        left: Math.min(...xs),
        top: Math.min(...ys),
        right: Math.max(...xs),
        bottom: Math.max(...ys),
    };
}

function fitTransform(
    bounds: SceneBounds,
    viewW: number,
    viewH: number,
    pivotX: number,
    pivotY: number,
    minZoom: number,
    maxZoom: number,
    scaleOverride?: number,
): Transform {
    const w = bounds.right - bounds.left;
    const h = bounds.bottom - bounds.top;
    const cx = bounds.left + w / 2;
    const cy = bounds.top + h / 2;

    let scale =
        scaleOverride ??
        Math.min(viewW / w, viewH / h) * FIT_PADDING;
    scale = Math.min(maxZoom, Math.max(minZoom, scale));

    return {
        scale,
        // Panzoom client space is relative to the SVG owner, not the window.
        x: pivotX - cx * scale,
        y: pivotY - cy * scale,
    };
}

type FocusFlight = {
    stillActive: () => boolean;
    waitZoomEnd: (instance: PanZoom) => Promise<void>;
    waitMs: (ms: number) => Promise<void>;
};

/** Coordinates cancelable focus waits so a superseded dolly does not hang. */
function createFocusController() {
    let token = 0;
    let settle: (() => void) | null = null;

    const interrupt = () => {
        const done = settle;
        settle = null;
        done?.();
    };

    return {
        begin(): FocusFlight {
            interrupt();
            const current = ++token;
            const stillActive = () => current === token;

            return {
                stillActive,
                waitZoomEnd(instance: PanZoom) {
                    return new Promise((resolve) => {
                        const finish = () => {
                            instance.off("zoomend", onEnd);
                            if (settle === finish) {
                                settle = null;
                            }
                            resolve();
                        };
                        const onEnd = () => finish();
                        settle = finish;
                        instance.on("zoomend", onEnd);
                    });
                },
                waitMs(ms: number) {
                    return new Promise((resolve) => {
                        let id = 0;
                        const finish = () => {
                            clearTimeout(id);
                            if (settle === finish) {
                                settle = null;
                            }
                            resolve();
                        };
                        settle = finish;
                        id = window.setTimeout(finish, ms);
                    });
                },
            };
        },
        cancel() {
            token += 1;
            interrupt();
        },
    };
}

async function focusOn(
    instance: PanZoom,
    svgRoot: SVGSVGElement,
    scene: SVGGElement,
    viewportEl: Element,
    groupEl: Element,
    focus: ReturnType<typeof createFocusController>,
) {
    if (!(groupEl instanceof SVGGraphicsElement)) {
        return;
    }

    const bounds = getSceneBounds(scene, groupEl);
    if (!bounds) {
        return;
    }

    // Fit to the visible clip (fullscreen main), not the SVG’s intrinsic
    // 2048×2048 box — otherwise scale overshoots and the target spills.
    const owner = svgRoot.getBoundingClientRect();
    const view = viewportEl.getBoundingClientRect();
    if (view.width === 0 || view.height === 0) {
        return;
    }

    const pivotX = view.left + view.width / 2 - owner.left;
    const pivotY = view.top + view.height / 2 - owner.top;

    const minZoom = instance.getMinZoom();
    const maxZoom = instance.getMaxZoom();
    const current = instance.getTransform();
    const final = fitTransform(
        bounds,
        view.width,
        view.height,
        pivotX,
        pivotY,
        minZoom,
        maxZoom,
    );

    const midScale = Math.max(
        minZoom,
        Math.min(current.scale, final.scale) * 0.5,
    );
    const mid = fitTransform(
        bounds,
        view.width,
        view.height,
        pivotX,
        pivotY,
        minZoom,
        maxZoom,
        midScale,
    );

    const flight = focus.begin();

    // Block wheel/drag so user input cannot desync the dolly phases.
    // Programmatic APIs still work while paused. Nav goto can still supersede.
    instance.pause();
    try {
        // Cancels a superseded flight’s smooth pan (panzoom has no public abort).
        instance.moveBy(0, 0, true);
        instance.smoothZoomAbs(pivotX, pivotY, midScale);
        await flight.waitZoomEnd(instance);
        if (!flight.stillActive()) {
            return;
        }

        const afterZoomOut = instance.getTransform();
        instance.moveBy(mid.x - afterZoomOut.x, mid.y - afterZoomOut.y, true);
        await flight.waitMs(SMOOTH_MS);
        if (!flight.stillActive()) {
            return;
        }

        instance.smoothZoomAbs(pivotX, pivotY, final.scale);
        await flight.waitZoomEnd(instance);
    } finally {
        // Only the active flight re-enables input; a superseding flight keeps pause.
        if (flight.stillActive()) {
            instance.resume();
        }
    }
}

Alpine.data('panzoom', () => ({
    cleanups: [] as (() => void)[],

    init() {
        const mainGroup = this.$el.querySelector('#main') as SVGGElement | null;
        if (!mainGroup) {
            return;
        }
        const svgRoot = mainGroup.ownerSVGElement;
        if (!svgRoot) {
            return;
        }

        const instance = panzoom(mainGroup as unknown as HTMLElement, {
            minZoom: 0.25,
            maxZoom: 10,
            // Clamp panning to the viewport so the scene can't be flung into
            // empty space. boundsPadding keeps ~20% of the scene on screen.
            bounds: true,
            boundsPadding: 0.01
        });

        instance.zoomTo(0, 0, 1);

        const store = Alpine.store('viewport') as ViewportStore;

        let writeId = 0;
        const writeTransform = () => {
            if (writeId) {
                return;
            }
            writeId = requestAnimationFrame(() => {
                writeId = 0;
                const t = instance.getTransform();
                store.x = t.x;
                store.y = t.y;
                store.scale = t.scale;
            });
        };

        writeTransform();
        instance.on('zoom', writeTransform);
        instance.on('pan', writeTransform);

        const focus = createFocusController();
        const onGoto = (e: Event) => {
            const id = (e as CustomEvent<{ id: string }>).detail?.id;
            if (!id) {
                return;
            }
            const groupEl = svgRoot.querySelector(`#${CSS.escape(id)}`);
            if (!groupEl) {
                return;
            }
            void focusOn(
                instance,
                svgRoot,
                mainGroup,
                this.$el,
                groupEl,
                focus,
            );
        };
        this.$el.addEventListener('viewport-goto', onGoto);

        const ctx = { instance, svgRoot, root: this.$el };
        for (const attach of modules) {
            this.cleanups.push(attach(ctx));
        }

        this.cleanups.push(() => {
            focus.cancel();
            instance.resume();
            this.$el.removeEventListener('viewport-goto', onGoto);
            if (writeId) {
                cancelAnimationFrame(writeId);
                writeId = 0;
            }
            instance.off('zoom', writeTransform);
            instance.off('pan', writeTransform);
            instance.dispose();
        });
    },

    destroy() {
        for (const cleanup of this.cleanups) {
            cleanup();
        }
        this.cleanups = [];
    }
}));
